import { useLang } from 'hooks/lang';

import {
  QuotationRequest,
  calculateProjectTotal,
  invoiceControlConfigByCode,
  invoiceControlStatusConfig,
  isInvoiceRepriceActive,
  isOwner,
  quotationRequestConfigByCode,
} from 'entities';

import { Column, Row, Collapse, Card, Table, Button, IconButton } from 'components/Blocks';
import { ChevronRightIcon } from 'components/Icons';
import { EntityStatusTag } from 'components/Entities';

import { CreateQuotationRequest } from './CreateModal';

import { useToggleV2, useUser } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { calculateAvailableAmount, verifyIfTheresAnAvailableAmount } from '../utils';
import { formatCurrency } from 'utils/text';

import { cn } from 'styles';

interface Props {
  action: (quotationRequest: QuotationRequest) => void;
}

export function QuotationRequests({ action }: Props) {
  const { t } = useLang();
  const user = useUser();

  const { project } = useProjectProfile();

  const { quotationRequests, type } = project.invoice;

  const hasQuotationRequestsToShow = !!quotationRequests.length;

  const availableAmount = calculateAvailableAmount(project);

  const [showRequests, vizHandlers] = useToggleV2(true);

  const isPackage = type === 'PACKAGE';

  const amountOk = isPackage
    ? [
        invoiceControlStatusConfig.readyToInvoice.code,
        invoiceControlStatusConfig.partiallyInvoiced.code,
      ].includes(project.invoice.status)
    : verifyIfTheresAnAvailableAmount(project);

  const canRequest = [
    isOwner(project.invoice, user),
    amountOk,
    !isInvoiceRepriceActive(project),
    invoiceControlConfigByCode[project?.invoice.status]?.canQuoteValues,
  ].every(Boolean);

  if (!canRequest && !hasQuotationRequestsToShow) return null;

  return (
    <Card flow="column">
      <Row justifyContent="space-between">
        <Row>
          <IconButton className="text-primary" onClick={vizHandlers.toggle}>
            <ChevronRightIcon className={cn('transition-transform', showRequests && 'rotate-90')} />
          </IconButton>

          <h6>{t('quotation-requests')}</h6>
        </Row>

        {canRequest && <CreateQuotationRequest />}
      </Row>

      {hasQuotationRequestsToShow ? (
        <Collapse expanded={showRequests} onlyRenderIfExpanded>
          <Column>
            <Table
              items={quotationRequests}
              uniqueKey="id"
              columns={[
                {
                  label: t('country'),
                  property: 'country',
                },
                {
                  label: t('client-name'),
                  property: 'clientName',
                },

                {
                  label: t('value'),
                  render: ({ valueInProjectCurrency, projectCurrency }) =>
                    formatCurrency(valueInProjectCurrency, projectCurrency),
                },

                {
                  label: t('quoted-value'),
                  render: ({ value, currency }) => (value ? formatCurrency(value, currency) : ''),
                },

                {
                  label: t('status'),
                  className: 'text-start',
                  filter: true,
                  getTextValue: ({ status }) => quotationRequestConfigByCode[status].getLabel(t),

                  render: ({ status }) => (
                    <EntityStatusTag target="quotationRequest" status={status} size="medium" />
                  ),
                },

                {
                  className: 'w-52',
                  render: (request) => {
                    const requestValue = Number(request.valueInProjectCurrency);

                    const canCreate = [
                      quotationRequestConfigByCode[request.status].canCreateInvoice,

                      isPackage
                        ? calculateProjectTotal(project.services) === requestValue.toFixed(2)
                        : availableAmount >= requestValue,
                    ].every(Boolean);

                    if (!canCreate) return null;

                    return (
                      <Button onClick={() => action(request)} variant="ghost">
                        {t(isPackage ? 'choose-package' : 'create-invoice')}
                      </Button>
                    );
                  },
                },
              ]}
            />
          </Column>
        </Collapse>
      ) : (
        !hasQuotationRequestsToShow && (
          <p className="text-center">{t('no-quotation-request-found')}</p>
        )
      )}
    </Card>
  );
}
